import * as React from "react";
import { StatusPill } from "./PageHeader";

// CheckList — a list of things that were MEASURED, each with a verdict.
//
// This shape recurs everywhere in this platform because the platform is built on it: cluster
// requirements, join steps, preflight, confcheck requirements, health checks, port audits. Every
// one is "a named thing, whether it passed, and what was actually found".
//
// Three states, not two. `unknown` exists because a check that could not gather evidence must
// never render as a pass — the confcheck bug this codebase already paid for was exactly that
// (`OK = r.Optional` reported a requirement satisfied when its source could not be read). A
// component that can only draw pass or fail forces every caller to lie in one direction, so it
// is expressed here once and correctly.
export type CheckState = "ok" | "warn" | "fail" | "unknown" | "pending";

export interface CheckItem {
  key: string;
  label: string;
  state: CheckState;
  /** What was actually measured — the server's own words, never a restatement of the label. */
  detail?: React.ReactNode;
  /** Why it matters, shown only when it is not passing: nobody needs the rationale for a pass. */
  because?: React.ReactNode;
}

const TONE: Record<CheckState, { tone: "ok" | "bad" | "warn" | "neutral"; text: string }> = {
  ok: { tone: "ok", text: "pass" },
  // A WARNING IS NOT A FAILURE, and the list had no way to say so — servers that answer
  // status:"warn" were collapsed into fail by every caller, because this type left them nowhere
  // else to go. Measured on oztag.click: DMARC at p=none and an absent MTA-STS are both advisory
  // and neither stops a message being delivered, yet the domain read as "deliverability all
  // failing" while its MX, SPF, DKIM and DNSSEC all passed.
  warn: { tone: "warn", text: "advisory" },
  fail: { tone: "bad", text: "fail" },
  // Deliberately distinct from both. "Not measured" is an absence of information, and painting it
  // as a failure cries wolf while painting it as a pass is the defect described above.
  unknown: { tone: "warn", text: "not measured" },
  pending: { tone: "neutral", text: "pending" },
};

export interface CheckListProps {
  items: CheckItem[];
  /** Shown when there is nothing to check — distinct from "everything passed". */
  emptyMessage?: string;
  /** Collapse passing rows, so a long list reads as its exceptions. Off by default. */
  summarise?: boolean;
}

export function CheckList({ items, emptyMessage = "Nothing to check.", summarise = false }: CheckListProps) {
  const [showAll, setShowAll] = React.useState(!summarise);
  if (items.length === 0) {
    return <div className="px-4 py-6 text-center text-[13px] text-muted">{emptyMessage}</div>;
  }
  const failing = items.filter((i) => i.state !== "ok");
  const shown = showAll ? items : failing;
  const hidden = items.length - shown.length;

  return (
    <div>
      <div className="divide-y divide-line">
        {shown.map((i) => {
          const t = TONE[i.state];
          return (
            <div key={i.key} className="flex items-start gap-3 px-4 py-2.5">
              <span className="mt-[2px] shrink-0">
                <StatusPill tone={t.tone}>{t.text}</StatusPill>
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px]">{i.label}</div>
                {i.detail != null && i.detail !== "" && (
                  <div className="mt-0.5 font-mono text-[11.5px] leading-relaxed text-muted">{i.detail}</div>
                )}
                {/* Only for a non-pass: the reason a thing matters is read when it is broken. */}
                {i.state !== "ok" && i.because != null && i.because !== "" && (
                  <div className="mt-1 text-[12px] leading-relaxed text-muted">{i.because}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {summarise && hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="w-full border-t border-line px-4 py-2 text-[12px] text-muted hover:bg-surface2"
        >
          {showAll ? "Show only what is not passing" : `Show ${hidden} passing check${hidden === 1 ? "" : "s"}`}
        </button>
      )}
      {shown.length === 0 && (
        <div className="px-4 py-6 text-center text-[13px] text-muted">
          All {items.length} checks pass.
        </div>
      )}
    </div>
  );
}
